import routes from 'constants/routes';

/**
 * The post-login return target.
 *
 * A URL fragment never reaches the server, so a deep link such as
 * /app/dashboard#disconnected-profiles loses its hash across a logged-out
 * login round trip. It is carried explicitly as a `returnPath` query param
 * instead.
 *
 * The value round-trips through an external origin and ends up assigned to
 * window.location, so only a same-origin relative path is ever honoured.
 */

const DEFAULT_RETURN_PATH = routes.DASHBOARD;

/** What the auth app sends when no returnPath was requested, or it rejected one. */
const NO_RETURN_PATH_REQUESTED = '/';

/**
 * Rejects absolute URLs, protocol-relative ones ("//evil.com"), any scheme,
 * and the backslash variants some browsers normalise to "/".
 */
export function isSafeReturnPath(
  path: string | null | undefined,
): path is string {
  if (!path || !path.startsWith('/')) {
    return false;
  }

  if (path.startsWith('//') || path.startsWith('/\\')) {
    return false;
  }

  // A genuinely relative path always resolves back onto the base origin.
  try {
    const probe = new URL(path, 'https://return-path.invalid');
    return probe.origin === 'https://return-path.invalid';
  } catch {
    return false;
  }
}

export function sanitizeReturnPath(path: string | null | undefined): string {
  if (!isSafeReturnPath(path) || path === NO_RETURN_PATH_REQUESTED) {
    return DEFAULT_RETURN_PATH;
  }

  return path;
}

/**
 * Router path -> full URL path. getCurrentReturnPath() strips the base, but
 * the value can also arrive from the auth SPA, which only checks that a path
 * is safe and passes any shape through -- so one that already carries the
 * base must not get a second copy.
 */
export function toAbsoluteReturnPath(
  returnPath: string,
  basePath: string,
): string {
  const base = basePath.replace(/\/$/, '');
  // The boundary matters: "/app/x", "/app?x" and "/app#x" already carry the
  // base, but "/application/x" is a different path that merely starts the same.
  const carriesBase =
    returnPath === base || /^[/?#]/.test(returnPath.slice(base.length));

  if (base === '' || (returnPath.startsWith(base) && carriesBase)) {
    return returnPath;
  }

  return `${base}${returnPath}`;
}

/** The current location as a return target, hash included. */
export function getCurrentReturnPath(basePath: string): string {
  const { pathname, search, hash } = window.location;
  const path = `${pathname}${search}${hash}`;

  // Stripped so the value stays a router path on both sides of the round trip.
  const withoutBase =
    basePath && path.startsWith(basePath) ? path.slice(basePath.length) : path;

  const normalized = withoutBase.startsWith('/')
    ? withoutBase
    : `/${withoutBase}`;

  return isSafeReturnPath(normalized) ? normalized : DEFAULT_RETURN_PATH;
}
